import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { z } from 'zod';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { COUNTRIES, DEFAULT_COUNTRY_CODE, isValidCountryCode } from '@/lib/auth/countries';
import { getMyTenant, TENANT_ME_QUERY_KEY, updateMyTenant, type MyTenant } from '@/lib/api/tenants';
import { cn } from '@/lib/utils';

import { OnboardingShell } from './OnboardingShell';
import { StepIndicator } from './StepIndicator';

const CURRENCIES = ['GBP', 'USD', 'EUR'] as const;
type Currency = (typeof CURRENCIES)[number];

const CompanyInfoSchema = z.object({
  legal_name: z.string().min(1, 'Legal name is required').max(200, 'Legal name is too long'),
  trading_name: z.string().max(200, 'Trading name is too long').optional().or(z.literal('')),
  currency: z.enum(CURRENCIES, { message: 'Pick a currency' }),
  country: z
    .string()
    .min(1, 'Pick a country')
    .refine(isValidCountryCode, 'Pick a supported country'),
});

type CompanyInfoValues = z.infer<typeof CompanyInfoSchema>;

/**
 * Default currency per country. The EU 27 all default to EUR; GB → GBP;
 * US → USD. Driven off the `COUNTRIES` list — anything in there that's
 * neither GB nor US falls through to EUR (true for the EU 27 list today).
 */
function defaultCurrencyForCountry(code: string): Currency {
  if (code === 'GB') return 'GBP';
  if (code === 'US') return 'USD';
  return 'EUR';
}

function tenantToFormValues(tenant: MyTenant | undefined): CompanyInfoValues {
  const country = tenant?.country ?? DEFAULT_COUNTRY_CODE;
  return {
    legal_name: tenant?.legal_name ?? '',
    trading_name: tenant?.trading_name ?? '',
    currency: tenant?.currency ?? defaultCurrencyForCountry(country),
    country,
  };
}

/**
 * Step 1 of 5 — company info. Captures the four fields the customs /
 * invoicing / labelling subsystems all read off the tenant:
 * `legal_name`, `trading_name` (optional), `currency`, `country`.
 *
 * Pre-fills from the cached tenant (the route loader populates the
 * `tenant/me` query before the component mounts), so navigating back
 * to step 1 after editing further shows the saved values.
 */
export function Step1CompanyInfo() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const tenantQuery = useQuery({
    queryKey: TENANT_ME_QUERY_KEY,
    queryFn: async () => {
      const result = await getMyTenant();
      if (result.isErr()) throw new Error(result.error.message);
      return result.value;
    },
  });
  const [topError, setTopError] = useState<string | null>(null);

  const form = useForm<CompanyInfoValues>({
    resolver: zodResolver(CompanyInfoSchema),
    mode: 'onChange',
    defaultValues: tenantToFormValues(tenantQuery.data),
  });

  // When the tenant query resolves (or refetches with new data), reset
  // the form to match — defaultValues only seed the initial render.
  useEffect(() => {
    if (tenantQuery.data) {
      form.reset(tenantToFormValues(tenantQuery.data));
    }
  }, [tenantQuery.data, form]);

  const submit = form.handleSubmit(async (values) => {
    setTopError(null);
    const result = await updateMyTenant({
      legal_name: values.legal_name,
      trading_name: values.trading_name?.length ? values.trading_name : undefined,
      currency: values.currency,
      country: values.country,
    });
    if (result.isErr()) {
      const message =
        result.error.kind === 'unauthorised'
          ? 'Your session expired. Please sign in again.'
          : "We couldn't save your company details. Please try again.";
      toast.error(message);
      setTopError(message);
      return;
    }
    await qc.invalidateQueries({ queryKey: TENANT_ME_QUERY_KEY });
    void navigate('/app/onboarding/step/2');
  });

  const isSubmitting = form.formState.isSubmitting;
  const legalNameError = form.formState.errors.legal_name?.message;
  const tradingNameError = form.formState.errors.trading_name?.message;

  return (
    <OnboardingShell>
      <StepIndicator currentStep={1} />
      <Card>
        <CardHeader>
          <CardTitle>Tell us about your business</CardTitle>
          <CardDescription>
            We use this to populate your customs declarations, invoice header, and shipping labels.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {topError ? (
            <Alert variant="destructive">
              <AlertDescription>{topError}</AlertDescription>
            </Alert>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="step1-legal-name">Legal name</Label>
            <Input
              id="step1-legal-name"
              autoComplete="organization"
              aria-invalid={legalNameError ? 'true' : undefined}
              aria-describedby={legalNameError ? 'step1-legal-name-error' : undefined}
              className={cn(legalNameError && 'border-destructive')}
              {...form.register('legal_name')}
            />
            {legalNameError ? (
              <p id="step1-legal-name-error" className="text-destructive text-xs">
                {legalNameError}
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="step1-trading-name">
              Trading name <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="step1-trading-name"
              autoComplete="organization"
              aria-invalid={tradingNameError ? 'true' : undefined}
              aria-describedby={tradingNameError ? 'step1-trading-name-error' : undefined}
              className={cn(tradingNameError && 'border-destructive')}
              {...form.register('trading_name')}
            />
            {tradingNameError ? (
              <p id="step1-trading-name-error" className="text-destructive text-xs">
                {tradingNameError}
              </p>
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="step1-country">Country</Label>
              <Controller
                control={form.control}
                name="country"
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={(next) => {
                      field.onChange(next);
                      // Keep currency aligned with country unless the
                      // user has already picked one explicitly.
                      form.setValue('currency', defaultCurrencyForCountry(next), {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                  >
                    <SelectTrigger id="step1-country" className="w-full">
                      <SelectValue placeholder="Select a country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          <span className="flex items-center gap-2.5">
                            <country.Flag
                              title={country.name}
                              style={{ width: 21, height: 14 }}
                              className="rounded-xs"
                            />
                            <span>{country.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="step1-currency">Currency</Label>
              <Controller
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="step1-currency" className="w-full">
                      <SelectValue placeholder="Select a currency" />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map((currency) => (
                        <SelectItem key={currency} value={currency}>
                          {currency}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              type="button"
              onClick={() => {
                void submit();
              }}
              disabled={isSubmitting || !form.formState.isValid}
            >
              {isSubmitting ? 'Saving…' : 'Save & continue'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </OnboardingShell>
  );
}
